import {useQuery} from "@tanstack/react-query";
import apiClient from "./apiClient";
import {config} from "../config";

export const DATA_STATUS_QUERY_KEY_ROOT = 'data-status';
export const DATA_STATUS_CACHE_TIME = 1000 * 60 * 60 * 36;
export const ONBOARDING_STATUS_POLL_INTERVAL = 8000;
const DATA_STATUS_MAX_RETRIES = 2;

export const getDataStatusQueryKey = (userId, activeAccountId) => [
    DATA_STATUS_QUERY_KEY_ROOT,
    userId,
    activeAccountId,
];

export const fetchDataStatus = async (requestConfig = {}) => {
    const response = await apiClient.get(
        `${config.REACT_APP_BACKEND_HOST}/configure/data/status`,
        requestConfig
    );
    return response.data;
};

export const invalidateDataStatusQuery = (queryClient, userId, activeAccountId) => {
    if (!queryClient) {
        return Promise.resolve();
    }

    const queryKey = userId && activeAccountId
        ? getDataStatusQueryKey(userId, activeAccountId)
        : userId
            ? [DATA_STATUS_QUERY_KEY_ROOT, userId]
            : [DATA_STATUS_QUERY_KEY_ROOT];

    return queryClient.invalidateQueries({queryKey});
};

export const shouldRetryDataStatusQuery = (failureCount, error) => {
    const status = error?.response?.status;

    if ([401, 403, 410, 429].includes(status) || error?.isGone || error?.isRateLimit) {
        return false;
    }

    return failureCount < DATA_STATUS_MAX_RETRIES;
};

export const dataStatusRefetchInterval = query => {
    const processingStatus = query?.state?.data?.dataStatus?.processing_status_new;
    const hasRunningBuild = Array.isArray(processingStatus) && processingStatus.some(entry => {
        const status = entry?.status || {};
        return typeof entry?.statusEntry === 'string'
            && entry.statusEntry.startsWith('root||')
            && Boolean(status.started)
            && !status.completed;
    });

    return hasRunningBuild ? ONBOARDING_STATUS_POLL_INTERVAL : false;
};

export const getDataStatusQueryOptions = ({onboardingActive = false} = {}) => ({
    staleTime: onboardingActive ? 0 : DATA_STATUS_CACHE_TIME,
    gcTime: DATA_STATUS_CACHE_TIME,
    refetchOnMount: onboardingActive ? 'always' : false,
    refetchInterval: onboardingActive ? ONBOARDING_STATUS_POLL_INTERVAL : dataStatusRefetchInterval,
    refetchIntervalInBackground: true,
});

export const useFetchDataStatus = (
    isAuthenticated,
    userId,
    activeAccountId,
    options = {},
) => {
    const queryOptions = getDataStatusQueryOptions(options);
    return useQuery({
        queryKey: getDataStatusQueryKey(userId, activeAccountId),
        queryFn: () => fetchDataStatus(),
        ...queryOptions,
        enabled: isAuthenticated && !!userId && !!activeAccountId,
        retry: shouldRetryDataStatusQuery,
    })
}
